import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { DialogTitle } from '@duck/ui'
import { GoogleSignInButton } from './GoogleSignInButton'
import { LinkedInSignInButton } from './LinkedInSignInButton'
import { EmailLoginForm } from './EmailLoginForm'
import { ResendVerificationForm } from '../ResendVerificationForm'
import { ForgotPasswordDialog } from '../ForgotPasswordDialog'
import { useTar } from '@/modules'
import { GoogleLoadingView, LinkedInLoadingView } from '../AuthComponents/AuthSocialLoadingViews'

type LoginViewProps = {
  /** Reports aggregate busy state up to the shared AuthDialog shell so it can
   *  block dismissal while a social redirect / email sign-in is in flight. */
  onBusyChange?: (busy: boolean) => void
}

export const LoginView = ({ onBusyChange }: LoginViewProps) => {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isLinkedInLoading, setIsLinkedInLoading] = useState(false)
  const [isEmailLoading, setIsEmailLoading] = useState(false)
  const [showResendForm, setShowResendForm] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [lastEmailAttempt, setLastEmailAttempt] = useState('')
  const { t } = useTar()

  const {
    signInWithGoogle,
    signInWithLinkedIn,
    signInWithEmail,
    openSignupDialog,
    error,
    errorCode,
    clearError
  } = useAuth()

  // Open to a clean slate: drop any login error left over from a previous open
  // so a stale message doesn't greet the user when the dialog reappears.
  useEffect(() => {
    clearError()
  }, [clearError])

  useEffect(() => {
    const resetSocialLoadingStates = () => {
      setIsGoogleLoading(false)
      setIsLinkedInLoading(false)
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resetSocialLoadingStates()
      }
    }

    window.addEventListener('pageshow', resetSocialLoadingStates)
    window.addEventListener('popstate', resetSocialLoadingStates)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('pageshow', resetSocialLoadingStates)
      window.removeEventListener('popstate', resetSocialLoadingStates)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isEmailLoading) return // Prevent submission if already loading

    const formData = new FormData(e.currentTarget)
    const email = formData.get('email') as string
    const password = formData.get('password') as string
    const rememberMe = formData.get('rememberMe') !== null

    // Store the email for potential resend use
    setLastEmailAttempt(email)

    if (signInWithEmail && email && password) {
      setIsEmailLoading(true)
      setShowResendForm(false) // Hide resend form on new attempt
      await signInWithEmail({ email, password, rememberMe })
      setIsEmailLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    if (isGoogleLoading) return // Prevent multiple clicks
    try {
      setIsGoogleLoading(true)
      if (signInWithGoogle) {
        await signInWithGoogle()
      }
    } catch (error) {
      console.error('Google sign-in error:', error)
      setIsGoogleLoading(false)
      throw error
    }
  }

  const handleLinkedInSignIn = async () => {
    if (isLinkedInLoading) return // Prevent multiple clicks
    try {
      setIsLinkedInLoading(true)
      if (signInWithLinkedIn) {
        await signInWithLinkedIn()
      }
    } catch (error) {
      console.error('LinkedIn sign-in error:', error)
      setIsLinkedInLoading(false)
      throw error
    }
  }

  const handleResendSuccess = () => {
    setShowResendForm(false)
    // Clear the error to hide the error message and return to login view
    clearError()
  }

  const handleResendCancel = () => {
    setShowResendForm(false)
  }

  const isUnverifiedEmailError = errorCode === 'unverifiedEmail'
  const isWrongAuthProviderError = errorCode === 'wrongAuthProvider'

  const isAnyLoading = isGoogleLoading || isLinkedInLoading || isEmailLoading

  useEffect(() => {
    onBusyChange?.(isAnyLoading)
  }, [isAnyLoading, onBusyChange])

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center gap-2 font-jet-mono text-[10.5px] uppercase tracking-[2px] text-accent">
          <span className="h-px w-3.5 bg-accent/70" />
          {t('auth.loginEyebrow')}
        </div>
        <DialogTitle className="mt-3 text-[26px] font-semibold leading-[1.12] tracking-[-0.6px] text-foreground">
          {t('common.login')}
        </DialogTitle>
        <p className="mt-2 max-w-[34ch] text-sm leading-relaxed text-muted-foreground">
          {t('auth.loginSubtitle')}
        </p>
      </div>

      {isLinkedInLoading ? (
        <LinkedInLoadingView />
      ) : isGoogleLoading ? (
        <GoogleLoadingView />
      ) : (
        <div className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <svg className="w-5 h-5 text-error me-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-error">
                    {isUnverifiedEmailError
                      ? t('auth.emailVerificationRequired')
                      : isWrongAuthProviderError
                        ? t('auth.wrongAuthProviderTitle')
                        : t('auth.loginError')}
                  </h4>
                  <p className="text-sm text-error mt-1">
                    {isWrongAuthProviderError ? t('auth.wrongAuthProvider') : error}
                  </p>
                </div>
              </div>

              {/* Show resend verification form for unverified email */}
              {isUnverifiedEmailError && (
                <div className="mt-3">
                  {!showResendForm ? (
                    <div className="space-y-2">
                      <p className="text-sm text-error">
                        {t('auth.emailVerificationDescription')}
                      </p>
                      <button
                        onClick={() => setShowResendForm(true)}
                        className="w-full bg-error hover:bg-error/90 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                      >
                        {t('auth.resendVerificationEmail')}
                      </button>
                    </div>
                  ) : (
                    <ResendVerificationForm
                      initialEmail={lastEmailAttempt}
                      onSuccess={handleResendSuccess}
                      onCancel={handleResendCancel}
                    />
                  )}
                </div>
              )}
            </div>
          )}
          <div className="flex flex-col space-y-3">
            {/* LinkedIn Sign-In Button */}
            <LinkedInSignInButton
              onClick={handleLinkedInSignIn}
              isDisabled={isAnyLoading}
            />
            {/* Google Sign-In Button */}
            <GoogleSignInButton
              onClick={handleGoogleSignIn}
              isDisabled={isAnyLoading}
            />
          </div>
          {/* Email Sign-In Form */}
          <EmailLoginForm
            onSubmit={handleEmailSubmit}
            isLoading={isEmailLoading}
            isDisabled={isAnyLoading}
            onSignUp={() => openSignupDialog()}
            onForgotPassword={() => setShowForgotPassword(true)}
          />
        </div>
      )}

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog
        open={showForgotPassword}
        onOpenChange={setShowForgotPassword}
        initialEmail={lastEmailAttempt}
      />
    </>
  )
}
